import * as React from 'react';
import { Check } from 'lucide-react';

import { cn } from '@design-system/lib/utils';

type StepStatus = 'completed' | 'current' | 'upcoming';

interface StepItem {
  id: string;
  label: React.ReactNode;
  status: StepStatus;
}

interface StepsProps extends Omit<React.ComponentProps<'nav'>, 'children'> {
  items: readonly StepItem[];
}

function Steps({ items, className, 'aria-label': ariaLabel = 'Progress', ...props }: StepsProps) {
  return (
    <nav
      aria-label={ariaLabel}
      className={cn('w-full', className)}
      {...props}
    >
      <ol className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
        {items.map(item => (
          <li
            key={item.id}
            data-status={item.status}
            aria-current={item.status === 'current' ? 'step' : undefined}
            className={cn(
              'flex items-center gap-2 text-sm',
              item.status === 'current' ? 'text-foreground font-semibold' : 'text-muted-foreground',
            )}
          >
            {item.status === 'completed' ? (
              <span
                aria-hidden="true"
                className="flex size-5 items-center justify-center rounded-full bg-green-600/5 text-green-800"
              >
                <Check className="size-3.5" />
              </span>
            ) : (
              <span
                aria-hidden="true"
                className={cn(
                  'size-4 rounded-full border',
                  item.status === 'current' ? 'border-primary border-4' : 'border-border',
                )}
              />
            )}
            <span>
              {item.status === 'completed' && <span className="sr-only">Completed: </span>}
              {item.label}
            </span>
          </li>
        ))}
      </ol>
    </nav>
  );
}

export { Steps, type StepItem, type StepsProps, type StepStatus };
